import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '@/lib/apiClient'
import { useApiClient } from '@/hooks/useApiClient'
import type { CommercialSignal } from './useCommercialSignals'

export type FocoTipo = 'compromiso_vencido' | 'compromiso' | 'reunion' | 'relacion'

export interface AgendaEvidence {
  source: string
  id: string
  date: string | null
  label: string
}

export interface AgendaFactor {
  code: string
  label: string
  points: number
}

export interface AgendaAction extends FocoItem {
  id: string
  personaId: string | null
  personaNombre: string | null
  prioridad: string | null
  fecha: string | null
  factors: AgendaFactor[]
  evidence: AgendaEvidence[]
  href: string
  command: { kind: 'commitment' | 'context'; id?: string }
}

export interface FocoItem {
  tipo: FocoTipo
  score: number
  titulo: string
  razon: string
  organizacionId: string | null
  organizacionNombre: string | null
  accionSugerida: string
  relacionadoId: string
  hora?: string | null
}

export interface CompromisoVencido {
  id: string
  title: string
  vencimiento: string
  diasVencido: number
  organizacionId: string | null
  organizacionNombre: string | null
}

export interface CompromisoTrabajo {
  id: string
  title: string
  vencimiento: string
  estado: string
  descripcion: string | null
  momento: 'vencido' | 'hoy' | 'proximo'
  organizacionId: string | null
  organizacionNombre: string | null
  personaId: string | null
  personaNombre: string | null
}

export interface RelacionEnfriando {
  organizacionId: string
  organizacionNombre: string
  estadoRelacion: string
  ultimaActividad: string | null
}

export interface MeetingContext {
  metodo: 'email_persona' | 'dominio' | 'nombre_persona' | 'nombre_empresa'
  personaId: string | null
  personaNombre: string | null
  organizacionId: string | null
  organizacionNombre: string | null
}

export interface Meeting {
  id: string
  asunto: string
  inicio: string
  fin: string | null
  todoElDia: boolean
  confirmada: boolean
  organizador: string | null
  asistentes: string[]
  contextoSugerido: MeetingContext | null
}

export interface TareaToDo {
  compromisoId?: string
  organizacionId?: string
  estadoSales?: string
  divergence?: boolean
  id: string
  listId: string
  title: string
  dueDate: string
  status: string
  listName: string
  momento: 'vencido' | 'hoy' | 'proximo'
}

export interface HoyPayload {
  fecha: string
  identidad: { nombre: string; email: string }
  graph: {
    calendario: 'ok' | 'error' | 'not_configured'
    todo: 'ok' | 'error' | 'not_configured'
  }
  diagnosticoToDo?: Record<string, string | number>
  synchronization?: {
    status: 'ok' | 'error' | 'not_requested'
    code?: string
    diagnostic?: Record<string, string | number | undefined>
  }
  sharePoint: {
    compromisos: 'ok' | 'error'
    notas?: 'ok' | 'error'
    vinculos?: 'ok' | 'error'
  }
  reuniones: Meeting[]
  agenda: {
    rulesVersion: string
    timeZone: string
    today: string
    missingSources: string[]
    total: number
    actions: AgendaAction[]
  }
  foco: FocoItem | null
  otrosFocos: FocoItem[]
  compromisosTrabajo: CompromisoTrabajo[]
  tareasToDo: TareaToDo[]
  compromisosVencidos: CompromisoVencido[]
  relacionesEnfriando: RelacionEnfriando[]
  commercialSignals: {
    rulesVersion: 'signals-v1'
    status: 'ok' | 'error'
    items: CommercialSignal[]
  }
}

export type HoyStatus =
  | 'loading'
  | 'ready'
  | 'not_signed_in'
  | 'not_configured'
  | 'forbidden'
  | 'unauthorized'
  | 'error'

interface HoyState {
  status: HoyStatus
  data: HoyPayload | null
  error: ApiError | Error | null
  reload: () => void
}

function statusFromError(err: unknown): HoyStatus {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'unauthorized'
    if (err.status === 403) return 'forbidden'
    if (err.status === 503) return 'not_configured'
  }
  return 'error'
}

export function useHoy(): HoyState {
  const { gate, makeClient } = useApiClient()
  const [data, setData] = useState<HoyPayload | null>(null)
  const [status, setStatus] = useState<HoyStatus>('loading')
  const [error, setError] = useState<ApiError | Error | null>(null)
  const [attempt, setAttempt] = useState(0)

  const reload = useCallback(() => setAttempt((v) => v + 1), [])

  useEffect(() => {
    if (gate !== 'ready') {
      setStatus(gate)
      return
    }

    const controller = new AbortController()
    const client = makeClient()
    if (!client) {
      setStatus('not_signed_in')
      return
    }

    setStatus('loading')
    setError(null)

    client
      .get<HoyPayload>('/api/hoy', { signal: controller.signal })
      .then((response) => {
        if (response.synchronization?.status === 'error') {
          // Safe runtime trace: no token, task content, email or other PII.
          // Serialize the already-sanitized diagnostic so Teams/Chromium
          // consoles retain the Graph status and endpoint instead of [Object].
          console.warn('hoy.todo_sync_failed', JSON.stringify(response.synchronization))
        }
        setData(response)
        setStatus('ready')
      })
      .catch((err: ApiError | Error) => {
        if (controller.signal.aborted) return
        setData(null)
        setError(err)
        setStatus(statusFromError(err))
      })

    return () => controller.abort()
  }, [gate, attempt, makeClient])

  return { status, data, error, reload }
}
